<script setup lang="ts">
import { useSchoolYears } from '@/composables/useSchoolYears';
import DropdownSelect from '@/core/elements/DropdownSelect.vue';
import { ISelectOption } from '@/interfaces';
import { getSchoolYearName } from '@/utils/schoolYear.utils';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { allSchoolYearsTenant } = useSchoolYears();

const emit = defineEmits<{
  'update:modelValue': [year: number | null];
}>();

const props = withDefaults(
  defineProps<{
    modelValue: number | null;
    theme?: 'inline' | 'input';
  }>(),
  {
    theme: 'inline',
  },
);
const { modelValue } = toRefs(props);

const selectedSchoolYearId = ref<number | null>(null);
const defaultOption = { label: t('schoolYears.all'), value: null };
const schoolYearOptions = computed<ISelectOption<number | null>[]>(() => [
  defaultOption,
  ...allSchoolYearsTenant.value.map((schoolYear) => ({
    label: getSchoolYearName(schoolYear, t),
    value: schoolYear.id,
  })),
]);

const handleYearChange = (year?: number | null): void => {
  selectedSchoolYearId.value = year ?? null;
  emit('update:modelValue', selectedSchoolYearId.value);
};

watch(
  [modelValue, schoolYearOptions],
  () => {
    if (!schoolYearOptions.value.length) return;
    selectedSchoolYearId.value =
      schoolYearOptions.value.find((option) => option.value === modelValue.value)?.value ?? null;
  },
  { immediate: true },
);
</script>

<template>
  <DropdownSelect
    v-if="schoolYearOptions.length"
    class="w-min text-body font-normal"
    :theme="theme"
    :model-value="selectedSchoolYearId"
    :options="schoolYearOptions"
    @update:model-value="handleYearChange"
  />
</template>
